import type {Location, ReverseGeocode} from '../../api/types';

/**
 * The `Places` namespace: turning a reverse-geocoded coordinate into the
 * strings a location surface renders.
 *
 * The derivation lives here rather than in either app because the app bar and
 * the location picker compose the same parts differently — the bar has room
 * for a title and a subordinate suffix, the picker card has a second line as
 * well — and they must never disagree about what the place is called. The row
 * you tap and the bar you land on say the same words because both read
 * `title`/`suffix` from here.
 */

/**
 * The three components the backend's reverse-geocode endpoint returns.
 *
 * Derived from the wire type rather than restated, so a rename on the backend
 * breaks this in one place instead of drifting silently.
 */
export type PlaceLabelParts = ReverseGeocode;

export type PlaceLabel = {
  /** Primary line. The city, or the region when there is no city. */
  title: string;
  /** Subordinate suffix rendered after `title`. Empty when there is none. */
  suffix: string;
  /**
   * Second line, for surfaces that have one. Empty when it would merely
   * repeat the title.
   */
  subtitle: string;
};

export const EMPTY_PLACE_LABEL: PlaceLabel = {
  title: '',
  suffix: '',
  subtitle: '',
};

const clean = (value: string | undefined | null): string =>
  (value ?? '').trim();

/**
 * Whether two cleaned parts name the same place: equal once case is folded,
 * accents kept. "Liège" and "Liege" stay distinct; "TORHOUT" and "Torhout"
 * do not.
 */
const sameName = (a: string, b: string): boolean =>
  a.toLocaleLowerCase() === b.toLocaleLowerCase();

/**
 * Compose the renderable label from the endpoint's parts.
 *
 * Four rules, each earned from a real place:
 *
 * - **The region is promoted when there is no city.** Rural Montana resolves
 *   to no `locality` at all. "Montana" is a truthful label; the nearest town
 *   is 52 km away and has genuinely different weather, so naming it would be
 *   worse than saying nothing.
 * - **A sub-locality never outlives its city.** Without the city to anchor it
 *   the suffix is unreadable, so it is dropped with it.
 * - **A region equal to the title is dropped.** Brooklyn's `locality` and
 *   `administrative_area_level_1` are both "New York"; keeping both spends
 *   the picker's second line repeating the first.
 * - **A sub-locality equal to the city is dropped.** Brussels' district is
 *   "Bruxelles" too (25 cells of the production cache, 2026-10-27, also
 *   Avelgem and Torhout); "Bruxelles · Bruxelles" says the name twice (#447).
 *
 * "Equal" is {@link sameName}: after trimming, ignoring case.
 */
export const buildPlaceLabel = (
  parts: Partial<PlaceLabelParts> | null | undefined,
): PlaceLabel => {
  const city = clean(parts?.city);
  const sublocality = clean(parts?.sublocality);
  const region = clean(parts?.region);

  const title = city || region;
  if (!title) {
    return EMPTY_PLACE_LABEL;
  }

  return {
    title,
    suffix: city && !sameName(sublocality, city) ? sublocality : '',
    subtitle: city && !sameName(region, city) ? region : '',
  };
};

/** Whether a label has anything worth rendering. */
export const hasPlaceLabel = (label: PlaceLabel | null | undefined): boolean =>
  Boolean(label?.title);

/**
 * The single rendering of `title` + `suffix`.
 *
 * Every surface joins the two parts through this. Hand-rolling the separator
 * per call site is exactly the drift this namespace exists to prevent — the
 * app bar and the picker row must not disagree about the punctuation any more
 * than they disagree about the name.
 */
export const formatPlaceTitle = (label: PlaceLabel): string =>
  label.suffix ? `${label.title} \u00b7 ${label.suffix}` : label.title;

/** What a Current location card knows about the GPS entry. */
export type CurrentLocationLabel = {
  /** The reverse-geocoded name, empty until resolved. */
  placeLabel: PlaceLabel;
  hasPlace: boolean;
  /** The raw position ("45.7640, 4.8357"), before or without a name. */
  coordinates: string | null;
  /**
   * The card's name: the place once resolved, else its coordinates. Null
   * without a GPS entry: each app says what to do then.
   */
  title: string | null;
  /**
   * Under a resolved name: its region when it says something the name does
   * not, else nothing. Never the coordinates: a place with a name is already
   * located, and the raw position under "Oslo · Sentrum" says nothing more
   * (option A, 2026-10-27). They only ever stand in for the `title`.
   */
  region: string | undefined;
};

/**
 * The Current location cards' words for the GPS entry, in both apps: each
 * card composes its own title and subtitle from them.
 */
export const currentLocationLabel = (
  userLocation: Location | null | undefined,
): CurrentLocationLabel => {
  const placeLabel = buildPlaceLabel(userLocation?.place);
  const hasPlace = hasPlaceLabel(placeLabel);
  const coordinates = userLocation
    ? `${userLocation.coordinates.latitude.toFixed(4)}, ${userLocation.coordinates.longitude.toFixed(4)}`
    : null;
  return {
    placeLabel,
    hasPlace,
    coordinates,
    title: hasPlace ? formatPlaceTitle(placeLabel) : coordinates,
    region: hasPlace ? placeLabel.subtitle || undefined : undefined,
  };
};
